import { ActionHistoryData, ActionHistoryModel } from '../action-history'
import { AuthorizationData, AuthorizationModel } from '../authorization'
import { AvailityServiceReview } from '../availity'
import { BaseModel } from '../base-model'
import { BaseModelData } from '../base-model-data'
import { Clinician, ClinicianData } from '../clinician'
import { IEventActor } from '../event/event-record.model'

export enum AuthorizationHistoryEventType {
  AuthCreated = 'C',
  AuthPulled = 'P',
  AuthSubmitted = 'S',
  AuthEdited = 'E',
  AuthDeleted = 'D',
  AuthPullFailed = 'F',
  AuthStatusInitiated = 'SI',
  AuthStatusUpdated = 'SU',
  AuthStatusCanceled = 'SC',
  AuthStatusFailed = 'SF',
}

/** What set an authorization event off. Mirrors the API's `AuthorizationEventTrigger`. */
export enum AuthorizationEventTrigger {
  Cron = 'cron',
  AppointmentCreation = 'appointmentCreation',
  AppointmentUpdated = 'appointmentUpdated',
  Manual = 'manual',
  PendingRerun = 'pendingRerun',
  User = 'user',
  ServiceReview = 'serviceReview',
  PortalSync = 'portalSync',
  Hl7 = 'hl7',
  Integration = 'integration',
  ScriptedTask = 'scriptedTask',
  System = 'system',
}

/** Which table an event was read from. Rows older than 2025-10-21 only exist in the legacy one. */
export enum AuthEventSource {
  PatientHistory = 'patientHistory',
  AuthorizationHistory = 'authorizationHistory',
}

/**
 * Widens the shared actor with the two types auth events use. `emr` marks a record that came
 * in through an EMR import rather than from a person or from our own automation.
 */
export type AuthorizationEventActorType = IEventActor['type'] | 'admin' | 'emr'

export interface AuthorizationEventActor {
  type: AuthorizationEventActorType
  id?: string
  ip?: string
  /** Resolved server-side when the actor is a clinician. */
  clinician?: ClinicianData
}

export type authorizationChange = Record<string, { oldValue: any, newValue: any }>

export interface AuthorizationHistory extends BaseModelData {
  patientId: string
  accountId?: string
  authorizationId: string
  authorization?: AuthorizationData
  actionHistories?: ActionHistoryData[]
  eventType: AuthorizationHistoryEventType
  change?: authorizationChange
  submittedData?: AvailityServiceReview.Review
  updatedBy?: string
  clinician?: ClinicianData
  source?: AuthEventSource
  /** PatientHistory log type. Absent on rows that came from the legacy table. */
  logType?: string
  /** Human-readable summary written with the PatientHistory row. Absent on legacy rows. */
  message?: string
  actor?: AuthorizationEventActor
  trigger?: AuthorizationEventTrigger
  /**
   * The portal as it was when the event happened. Absent on legacy rows, which never stored
   * it and have to fall back to the authorization's current portal.
   */
  portal?: string
  /** Failure detail recorded with the event itself, independent of any ActionHistory join. */
  errors?: string
}

export class AuthorizationHistoryModel extends BaseModel {
  patientId: string
  authorizationId: string
  authorization?: AuthorizationModel
  actionHistories?: ActionHistoryModel[]
  eventType: AuthorizationHistoryEventType
  change?: authorizationChange
  submittedData?: AvailityServiceReview.Review
  updatedBy?: string
  clinician?: Clinician
  source?: AuthEventSource
  logType?: string
  message?: string
  actor?: AuthorizationEventActor
  trigger?: AuthorizationEventTrigger
  portal?: string
  errors?: string

  constructor(data: AuthorizationHistory) {
    super(data, true)

    if (data.authorization) {
      this.authorization = new AuthorizationModel(data.authorization)
    }

    if (data.actionHistories) {
      this.actionHistories = data.actionHistories.map((ah) => new ActionHistoryModel(ah))
    }

    if (data.clinician) {
      this.clinician = new Clinician(data.clinician)
    }
  }
}
